// RecordAudit — instance of a ChecklistDefinition (with frozen snapshot, participants,
// task results, signatures, score, lifecycle: create → submit → close → sign → reopen).
// BE: projects/dms/src/Domain/Checklist/RecordAudit/ in backend-monorepo.
import {z} from 'zod';

import {RecordIdHttpModel, RecordIdHttpModelSchema} from '@dms/api/shared';

export const recordAuditStateSchema = z.enum(['OPEN', 'CLOSED', 'SIGNED']);
export type RecordAuditState = z.infer<typeof recordAuditStateSchema>;

export const recordAuditResultValueSchema = z.enum(['OK', 'BAD', 'NA']);
export type RecordAuditResultValue = z.infer<typeof recordAuditResultValueSchema>;

export const participantRequestBodySchema = z.object({
  employeeId: z.string(),
  professionId: z.string(),
});
export type ParticipantRequestBody = z.infer<typeof participantRequestBodySchema>;

/**
 * The 7 OMN resources the audit can pair with (BR-5). For the POC only
 * `VEHICLE` is exercised end-to-end; the other six are tabbed but disabled
 * until the corresponding picker tabs are implemented.
 */
export const auditPairableResourceIdSchema = z.enum([
  'BUSINESS_CASE',
  'SERVICE_ORDER',
  'RENTAL_ORDER',
  'VEHICLE',
  'CUSTOMER',
  'INTEREST',
  'WORKSHOP_INTEREST',
]);
export type AuditPairableResourceId = z.infer<typeof auditPairableResourceIdSchema>;

export type RecordAuditBranchResponseBody = {
  id: string;
  name: string;
  code: string;
};

export type RecordAuditAuditorResponseBody = {
  id: string;
  firstName: string;
  lastName: string;
};

export type RecordAuditScoreResponseBody = {
  okPoints: number;
  maxPoints: number;
  percentage: number | null;
  passed: boolean | null;
};

export type RecordAuditStatisticsResponseBody = {
  okCount: number;
  badCount: number;
  naCount: number;
  totalCount: number;
  score: RecordAuditScoreResponseBody;
};

export type RecordAuditSectionSnapResponseBody = {
  originalSectionId: string;
  name: string;
  position: number;
};

export type RecordAuditTaskSnapResponseBody = {
  originalTaskDefinitionId: string;
  sectionId: string | null;
  text: string;
  weight: number;
  position: number;
  fileRequired: boolean;
  isMandatory: boolean;
};

export type RecordAuditDefinitionSnapshotResponseBody = {
  recordAuditDefinitionId: string;
  name: string;
  category: string | null;
  description: string | null;
  successThreshold: number;
  sections: RecordAuditSectionSnapResponseBody[];
  tasks: RecordAuditTaskSnapResponseBody[];
};

export type RecordAuditFileType = 'image' | 'video' | 'unknown';

export type RecordAuditTaskResultFileMetadata = {
  originalFilename: string;
  fileType: RecordAuditFileType;
  originalUri: string;
  dimensions: {width: number; height: number} | null;
};

export type RecordAuditTaskResultFileResponseBody = {
  id: string;
  metadata: RecordAuditTaskResultFileMetadata;
};

export type RecordAuditTaskResultResponseBody = {
  originalTaskDefinitionId: string;
  value: RecordAuditResultValue | null;
  note: string | null;
  files: RecordAuditTaskResultFileResponseBody[];
  updatedAt: string;
};

export type RecordAuditParticipantResponseBody = {
  employee: RecordAuditAuditorResponseBody;
  professionId: string;
};

export type RecordAuditSignatureResponseBody = {
  fileId: string;
  signedAt: string;
};

export type RecordAuditResponseBody = {
  id: string;
  state: RecordAuditState;
  auditNumber: string;
  // False when the number comes from a record-type series; branch and linked record are then fixed.
  canChangeBranchAndRecord: boolean;
  branch: RecordAuditBranchResponseBody;
  auditor: RecordAuditAuditorResponseBody | null;
  responsiblePerson: RecordAuditAuditorResponseBody | null;
  date: string;
  recordId: RecordIdHttpModel | null;
  definitionSnapshot: RecordAuditDefinitionSnapshotResponseBody;
  taskResults: RecordAuditTaskResultResponseBody[];
  participants: RecordAuditParticipantResponseBody[];
  statistics: RecordAuditStatisticsResponseBody;
  auditorSignature: RecordAuditSignatureResponseBody | null;
  responsibleSignature: RecordAuditSignatureResponseBody | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  reopenedAt: string | null;
  signedAt: string | null;
};

/**
 * Merged task = snapshot task + its current task result joined by
 * `originalTaskDefinitionId`. Drives both the read-only body (POC-04) and the
 * editable form (POC-05). The originalTaskDefinitionId is also the URL param
 * for the per-result autosave PUT endpoint (full-replacement).
 */
export type RecordAuditTask = {
  originalTaskDefinitionId: string;
  sectionId: string | null;
  text: string;
  weight: number;
  position: number;
  fileRequired: boolean;
  isMandatory: boolean;
  value: RecordAuditResultValue | null;
  note: string | null;
  files: RecordAuditTaskResultFileResponseBody[];
  updatedAt: string | null;
};

export const mergeRecordAuditTasks = (audit: RecordAuditResponseBody): RecordAuditTask[] => {
  const resultsByTaskId = new Map<string, RecordAuditTaskResultResponseBody>();
  for (const result of audit.taskResults) {
    resultsByTaskId.set(result.originalTaskDefinitionId, result);
  }
  return audit.definitionSnapshot.tasks.map((task) => {
    const result = resultsByTaskId.get(task.originalTaskDefinitionId);
    return {
      originalTaskDefinitionId: task.originalTaskDefinitionId,
      sectionId: task.sectionId,
      text: task.text,
      weight: task.weight,
      position: task.position,
      fileRequired: task.fileRequired,
      isMandatory: task.isMandatory,
      value: result?.value ?? null,
      note: result?.note ?? null,
      files: result?.files ?? [],
      updatedAt: result?.updatedAt ?? null,
    };
  });
};

export const createRecordAuditRequestBodySchema = z.object({
  recordAuditDefinitionId: z.string(),
  branchId: z.string(),
  auditorEmployeeId: z.string().nullish(),
  responsiblePersonEmployeeId: z.string().nullish(),
  date: z.string(),
  participants: z.array(participantRequestBodySchema).optional(),
  recordId: RecordIdHttpModelSchema.nullish(),
});
export type CreateRecordAuditRequestBody = z.infer<typeof createRecordAuditRequestBodySchema>;

export type CreateRecordAuditApiResponse = /** status 201 */ {
  id: string;
};
export const createRecordAuditApiArgSchema = z.object({
  body: createRecordAuditRequestBodySchema,
});
export type CreateRecordAuditApiArg = z.infer<typeof createRecordAuditApiArgSchema>;

export const updateRecordAuditHeaderRequestBodySchema = z.object({
  branchId: z.string(),
  auditorEmployeeId: z.string().nullish(),
  responsiblePersonEmployeeId: z.string().nullable(),
  date: z.string(),
  participants: z.array(participantRequestBodySchema).optional(),
  recordId: RecordIdHttpModelSchema.nullish(),
});
export type UpdateRecordAuditHeaderRequestBody = z.infer<
  typeof updateRecordAuditHeaderRequestBodySchema
>;

export type UpdateRecordAuditHeaderApiResponse = unknown;
export const updateRecordAuditHeaderApiArgSchema = z.object({
  auditId: z.string(),
  body: updateRecordAuditHeaderRequestBodySchema,
});
export type UpdateRecordAuditHeaderApiArg = z.infer<typeof updateRecordAuditHeaderApiArgSchema>;

export type GetRecordAuditApiResponse = /** status 200 */ RecordAuditResponseBody;
export const getRecordAuditApiArgSchema = z.object({
  auditId: z.string(),
});
export type GetRecordAuditApiArg = z.infer<typeof getRecordAuditApiArgSchema>;

// List every audit paired to a single record (BE: ListRecordAuditsByRecordController,
// GET /dms/v1/record-audit/audit?resourceId=&recordId=). Both params are required.
export type ListRecordAuditsByRecordApiResponse = /** status 200 */ RecordAuditResponseBody[];
export const listRecordAuditsByRecordApiArgSchema = z.object({
  resourceId: z.string(),
  recordId: z.string(),
});
export type ListRecordAuditsByRecordApiArg = z.infer<typeof listRecordAuditsByRecordApiArgSchema>;

/**
 * PATCH (BE T20-120660): every field is optional. Omitted = untouched.
 * `value: null` and `note: null` clear the stored result; `fileIds: []` clears
 * attachments.
 */
export const submitRecordAuditResultRequestBodySchema = z.object({
  value: recordAuditResultValueSchema.nullable().optional(),
  note: z.string().nullable().optional(),
  fileIds: z.array(z.string()).optional(),
});
export type SubmitRecordAuditResultRequestBody = z.infer<
  typeof submitRecordAuditResultRequestBodySchema
>;

// PATCH returns the updated task result + recomputed statistics (not the full audit).
export type SubmitRecordAuditResultApiResponse = {
  taskResult: RecordAuditTaskResultResponseBody;
  statistics: RecordAuditStatisticsResponseBody;
};
export const submitRecordAuditResultApiArgSchema = z.object({
  auditId: z.string(),
  originalTaskDefinitionId: z.string(),
  body: submitRecordAuditResultRequestBodySchema,
});
export type SubmitRecordAuditResultApiArg = z.infer<typeof submitRecordAuditResultApiArgSchema>;

export type CloseRecordAuditApiResponse = unknown;
export const closeRecordAuditApiArgSchema = z.object({
  auditId: z.string(),
});
export type CloseRecordAuditApiArg = z.infer<typeof closeRecordAuditApiArgSchema>;

export type ReopenRecordAuditApiResponse = unknown;
export const reopenRecordAuditApiArgSchema = z.object({
  auditId: z.string(),
});
export type ReopenRecordAuditApiArg = z.infer<typeof reopenRecordAuditApiArgSchema>;

export type DeleteRecordAuditApiResponse = unknown;
export const deleteRecordAuditApiArgSchema = z.object({
  auditId: z.string(),
});
export type DeleteRecordAuditApiArg = z.infer<typeof deleteRecordAuditApiArgSchema>;

export const signRecordAuditRequestBodySchema = z.object({
  auditorSignatureFileId: z.string(),
  responsibleSignatureFileId: z.string(),
});
export type SignRecordAuditRequestBody = z.infer<typeof signRecordAuditRequestBodySchema>;

export type SignRecordAuditApiResponse = unknown;
export const signRecordAuditApiArgSchema = z.object({
  auditId: z.string(),
  body: signRecordAuditRequestBodySchema,
});
export type SignRecordAuditApiArg = z.infer<typeof signRecordAuditApiArgSchema>;

export type ExportRecordAuditPdfApiResponse = Blob;
export const exportRecordAuditPdfApiArgSchema = z.object({
  auditId: z.string(),
});
export type ExportRecordAuditPdfApiArg = z.infer<typeof exportRecordAuditPdfApiArgSchema>;

/**
 * RecordAuditNote — reusable note template (T20-120660). Free-floating, no association
 * to a specific checklist or audit. Displayed as clickable pills under each
 * task's note textarea; clicking a pill replaces the textarea content.
 */
export type RecordAuditNoteResponseBody = {
  id: string;
  text: string;
};

export type GetRecordAuditNotesApiResponse = RecordAuditNoteResponseBody[];
export type GetRecordAuditNotesApiArg = void;

export const createRecordAuditNoteRequestBodySchema = z.object({
  text: z.string(),
});
export type CreateRecordAuditNoteRequestBody = z.infer<
  typeof createRecordAuditNoteRequestBodySchema
>;
export type CreateRecordAuditNoteApiResponse = RecordAuditNoteResponseBody;
export const createRecordAuditNoteApiArgSchema = z.object({
  body: createRecordAuditNoteRequestBodySchema,
});
export type CreateRecordAuditNoteApiArg = z.infer<typeof createRecordAuditNoteApiArgSchema>;

export type DeleteRecordAuditNoteApiResponse = unknown;
export const deleteRecordAuditNoteApiArgSchema = z.object({
  noteId: z.string(),
});
export type DeleteRecordAuditNoteApiArg = z.infer<typeof deleteRecordAuditNoteApiArgSchema>;
